import { ReactElement, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ConfigurationUpdate, FiltersUpdate, SortUpdate, StateUpdate, tableColumns, TableState } from "./table-data";
import { useDependency } from "../../contexts/DependencyContext";
import { IComponent } from "../../services/http/component/models/IComponent";
import { IBranch } from "../../services/http/component/models/IBranch";
import { SearchComponentsPaginatedParameters } from "../../services/http/component/models/SearchComponentsPaginatedParameters";
import { SearchComponentsPaginatedResult } from "../../services/http/component/models/SearchComponentsPaginatedResult";
import { MeasureListService } from "../../services/http/measurelist/MeasureListService";
import { GetMeasuresParameters } from "../../services/http/measurelist/models/GetMeasuresParameters";
import { ComponentService } from "../../services/http/component/ComponentService";
import { ComponentRowViewModel, ComponentTableViewModel } from "./ComponentTableViewModel";
import { className } from "../../shared/helpers";
import ResultsTable from "./ResultsTable";
import Filters, { DensityStates } from "./Filters";
import ComponentSidePanel from "./ComponentSidePanel";
import styles from "./ComponentsPage.module.scss";
import { useTimeMachineUpdate } from "../../contexts/TimeMachineContext";

export const densityStorageKey = "table_collapsed";
let previousPage: number;

const ComponentsPage = (): ReactElement =>
{
    const componentService = useDependency(ComponentService);
    const measureListService = useDependency(MeasureListService);
    const [selectedComponent, setSelectedComponent] = useState<IComponent>(null as any);
    const [loadingInitialData, setLoadingInitialData] = useState<boolean>(true);
    const [loadingMeasures, setLoadingMeasures] = useState<boolean>(true);
    const [clientLoading, setClientLoading] = useState<boolean>(true);
    const timeMachineUpdate = useTimeMachineUpdate();

    const [tableState, setTableState] = useState<TableState>({
        data: new ComponentTableViewModel(),
        filters: null as any,
        configuration: {
            density: localStorage.getItem(densityStorageKey) ?? DensityStates.Expanded,
            visibleSections: tableColumns.filter(x => x.visible !== undefined)
        }
    });

    function updateTableState<TData>(state: StateUpdate, data: TData): void
    {
        switch (state)
        {
            case StateUpdate.Filters:
                setTableState({ ...tableState, filters: { ...tableState.filters, ...data } });
                break;
            case StateUpdate.Configuration:
                setTableState({ ...tableState, configuration: { ...tableState.configuration, ...data } });
                break;
            default:
                break;
        }
    }

    function selectComponent(component: IComponent): void
    {
        setSelectedComponent(component.id !== selectedComponent?.id ? component : (null as any));
    };

    function getBranches(branchId: number): IBranch[] | undefined
    {
        if (!tableState.data)
        {
            return undefined;
        }

        const component = tableState.data.rows.find(x => x.component.branchId === branchId);

        if (component && component.branches && component.branches.length > 0)
        {
            return tableState.data.getBranches(component.branches, branchId);
        }
    };

    function setBreadcrumb(componentRow: ComponentRowViewModel, full?: boolean): ReactElement
    {
        const branchList = componentRow.branches.filter(x => x.level > 1 && x.level < componentRow.component.level)
            .sort((a, b) => a.level > b.level ? 1 : -1)
            .map(x => x);

        if (!full)
        {
            const component = branchList[branchList.length - 1];
            return component && <Link to={`/dashboard/${component.componentId}`}>{`... › ${component.componentName}`}</Link>;
        }
        else
        {
            return branchList &&(
                <>
                    {branchList.map((x, i) => (
                         <Link key={i} to={`/dashboard/${x.componentId}`}>{x.componentName}</Link>
                    ))}
                </>
            )
        }
    }

    useEffect(() =>
    {
        timeMachineUpdate(undefined!);
        // Set initial parameters
        if (!tableState.filters)
        {
            const paramsInstance = new SearchComponentsPaginatedParameters();
            const filters = { ...paramsInstance, pageNumber: 1, pageSize: 20 };
            setTableState({ ...tableState, filters });
            return;
        }

        if (previousPage && previousPage === tableState.filters.pageNumber)
        {
            // Reset data if filters are applied
            tableState.data.clearRows();
            tableState.filters.pageNumber = 1;
            // Clear selected component if there's any
            setSelectedComponent(null as any);
        }

        (async (): Promise<void> =>
        {
            try
            {
                // Get updated data
                const updatedData = await componentService.search(tableState.filters);
                
                // End loading
                if (loadingInitialData) setLoadingInitialData(false);
                setLoadingMeasures(true);

                // Update state with fetched data if there's any
                setTableState({ ...tableState, data: tableState.data.setComponents(updatedData) });

                // Set datasource IDs
                const parameters = new GetMeasuresParameters();
                parameters.dataSourceIds = tableState.data.getNewRows().map((x: ComponentRowViewModel) => x.dataSourceId).filter(x => x);

                if (parameters.dataSourceIds.length === 0)
                {
                    setLoadingMeasures(false);
                    setClientLoading(false);
                    return;
                }

                // Load aggregations and manage loading state
                const measures = await measureListService.getAggregations(parameters);
                setTableState({ ...tableState, data: tableState.data.setMeasures(measures) });
                setLoadingMeasures(false);
                setClientLoading(false);
                previousPage = tableState.filters.pageNumber;
            }
            catch (e)
            {
                setTableState({ ...tableState, data: new ComponentTableViewModel().setComponents(new SearchComponentsPaginatedResult())});
                setLoadingInitialData(false);
                setLoadingMeasures(false);
                setClientLoading(false);
                console.error(e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState?.filters]);

    return (
        <main {...className(styles.main, {
            [styles.disabled]: loadingInitialData || loadingMeasures
        })}>
            {loadingInitialData && <div className="loading" />}

            {!loadingInitialData && (
                <div className={styles.tableWrapper}>
                    <header {...className({ [styles.componentSelected]: selectedComponent })}>
                        <h3>All components</h3>

                        <Filters
                            tableState={tableState}
                            setTableFilters={filters => updateTableState<FiltersUpdate>(StateUpdate.Filters, filters)}
                            setTableConfiguration={configuration => updateTableState<ConfigurationUpdate>(StateUpdate.Configuration, configuration)}
                            isLoading={loadingMeasures || loadingInitialData || clientLoading}
                            setClientLoading={setClientLoading}
                        />
                    </header>

                    <ResultsTable
                        tableColumns={tableColumns}
                        tableState={tableState}
                        tableLoadingState={loadingMeasures || clientLoading}
                        setTableLoadingState={setClientLoading}
                        selectedComponent={selectedComponent}
                        selectComponent={selectComponent}
                        setBreadcrumb={setBreadcrumb}
                        setPageNumber={(pageNumber: number) => updateTableState<FiltersUpdate>(StateUpdate.Filters, { pageNumber })}
                        setSortData={(sortData: SortUpdate) => updateTableState<FiltersUpdate>(StateUpdate.Filters, sortData)}
                    />
                </div>
            )}

            {!loadingInitialData && selectedComponent && (
                <ComponentSidePanel
                    componentRow={tableState.data.rows.find(x => x.component.branchId === selectedComponent.branchId)}
                    component={selectedComponent}
                    getBranches={getBranches}
                    setBreadcrumb={setBreadcrumb}
                    close={() => setSelectedComponent(null as any)}
                />
            )}
        </main>
    );
};

export default ComponentsPage;
